import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Send, ShieldOff } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { elAPI, elErr } from '../../lib/elAPI';

const yesNo = (v, yes, no) => (v === null || v === undefined ? '—' : v ? yes : no);

const SendDialog = ({ clientId, onClose, onSent }) => {
  const [kind, setKind] = useState('addendum');
  const [busy, setBusy] = useState(false);
  const send = () => {
    setBusy(true);
    elAPI.sendUpdated(clientId, { kind }).then(() => { toast.success('Sent for signature'); onSent(); onClose(); }).catch((e) => toast.error(elErr(e))).finally(() => setBusy(false));
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="send-updated-dialog">
        <DialogHeader><DialogTitle>Send updated engagement letter</DialogTitle></DialogHeader>
        {[['addendum', 'Supplementary disclosure & consent addendum', 'Only the AI-use and offshore clauses with signed acknowledgements.'],
          ['full', 'Full new engagement letter', 'Template picked by entity type, with the client’s current services and fees.']].map(([k, l, d]) => (
          <button key={k} type="button" onClick={() => setKind(k)} data-testid={`send-updated-kind-${k}`} className={`w-full rounded-md border p-3 text-left transition-colors ${kind === k ? 'border-primary bg-primary/5' : 'hover:bg-muted'}`}>
            <p className="text-sm font-medium">{l}</p><p className="text-xs text-muted-foreground">{d}</p></button>))}
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button disabled={busy} onClick={send} data-testid="send-updated-confirm"><Send className="mr-1 h-4 w-4" />Send</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RestrictionDialog = ({ clientId, on, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="restriction-dialog">
        <DialogHeader><DialogTitle>{on ? 'Remove' : 'Apply'} “No offshore access”</DialogTitle></DialogHeader>
        <Input placeholder="Reason (required, audited)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="restriction-reason" />
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={reason.trim().length < 5} onClick={() => elAPI.setRestriction(clientId, { on: !on, reason }).then(() => { toast.success('Restriction updated'); onDone(); onClose(); }).catch((e) => toast.error(elErr(e)))} data-testid="restriction-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ConsentPanel = ({ clientId, role, onSent, onRestrictionChange }) => {
  const [d, setD] = useState(null);
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => elAPI.consents(clientId).then(setD).catch(() => setD({ consents: [] })), [clientId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const restricted = d.offshore_restricted?.on;
  const admin = ['owner', 'admin'].includes(role);
  const canSend = ['owner', 'admin', 'accountant'].includes(role);
  return (
    <Card data-testid="consent-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-3 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">AI &amp; offshore consent
          {restricted && <Badge variant="outline" className="border-rose-300 text-rose-700" data-testid="consent-no-offshore-badge"><ShieldOff className="mr-1 h-3 w-3" />No offshore access</Badge>}</CardTitle>
        <div className="flex gap-2">
          {admin && <Button size="sm" variant="outline" onClick={() => setDlg('restriction')} data-testid="restriction-edit">{restricted ? 'Remove restriction' : 'Restrict offshore access'}</Button>}
          {canSend && <Button size="sm" onClick={() => setDlg('send')} data-testid="send-updated-letter"><Send className="mr-1 h-4 w-4" />Send updated engagement letter</Button>}
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {restricted && <p className="text-xs text-muted-foreground" data-testid="restriction-reason-text">{d.offshore_restricted.reason}</p>}
        {d.consents.length ? d.consents.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2" data-testid={`consent-${c.id}`}>
            <span>{c.signer_name} · {c.signed_at ? new Date(c.signed_at).toLocaleDateString('en-AU') : ''} · {c.template_key}</span>
            <span className="text-xs">AI: <b>{yesNo(c.ai_acknowledged, 'Acknowledged', 'Not ticked')}</b> · Offshore: <b>{yesNo(c.offshore_authorised, 'Authorised', 'Declined')}</b></span>
          </div>)) : <p className="text-xs text-muted-foreground">No signed AI/offshore acknowledgements yet. An older signed letter does not count as offshore consent.</p>}
      </CardContent>
      {dlg === 'send' && <SendDialog clientId={clientId} onClose={() => setDlg(null)} onSent={() => { load(); onSent?.(); }} />}
      {dlg === 'restriction' && <RestrictionDialog clientId={clientId} on={restricted} onClose={() => setDlg(null)} onDone={() => { load(); onRestrictionChange?.(); }} />}
    </Card>
  );
};
